import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Check, CheckCircle2, ChevronDown, ChevronUp, Loader2, Pencil, RotateCcw, Trash2, XCircle } from "lucide-react";
import { apiJson } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReportReadyEditorModal } from "./ReportReadyEditorModal";

const PENDING_PAGE_SIZE = 30;

interface ReportReadyListItem {
  id: number;
  sourceEvidenceAssetId: number;
  storeName: string;
  operatorName: string;
  secureUrl: string | null;
  sourceSecureUrl: string | null;
  updatedAt: string;
  cropRecipe?: {
    crop?: {
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
  } | null;
}

interface ReportReadyPendingItem {
  sourceEvidenceAssetId: number;
  storeName: string;
  operatorName: string;
  secureUrl: string | null;
}

interface ReportReadyExcludedItem extends ReportReadyPendingItem {
  exclusionId: number;
  excludedAt: string;
  reason: string | null;
}

interface ReportReadyListResponse {
  success: boolean;
  data: {
    summary: {
      retailerReadyCount: number;
      reportReadyCount: number;
      excludedCount: number;
      pendingPreparation: number;
    };
    excluded: ReportReadyExcludedItem[];
    items: ReportReadyListItem[];
    pending: {
      items: ReportReadyPendingItem[];
      pagination: {
        limit: number;
        nextCursor: string | null;
        hasMore: boolean;
      };
    };
  };
}

type EditorState =
  | {
      mode: "create";
      sourceEvidenceAssetId: number;
      originalImageUrl: string;
      storeName: string;
      operatorName: string;
    }
  | {
      mode: "edit";
      reportReadyImageId: number;
      sourceEvidenceAssetId: number;
      originalImageUrl: string;
      storeName: string;
      operatorName: string;
    }
  | null;

export function ReportReadyImagesTab({ campaignId }: { campaignId: number }) {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState>(null);
  const [extraPendingItems, setExtraPendingItems] = useState<ReportReadyPendingItem[]>([]);
  const [pendingNextCursor, setPendingNextCursor] = useState<string | null>(null);
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const [loadingMorePending, setLoadingMorePending] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [includedCollapsed, setIncludedCollapsed] = useState(false);
  const [excludedCollapsed, setExcludedCollapsed] = useState(true);
  const [excludeCandidate, setExcludeCandidate] = useState<ReportReadyPendingItem | null>(null);
  const [removeCandidate, setRemoveCandidate] = useState<ReportReadyListItem | null>(null);
  const { toast } = useToast();
  const [savingAssetId, setSavingAssetId] = useState<number | null>(null);

  const addAsIsMutation = useMutation({
    mutationFn: async (sourceEvidenceAssetId: number) => {
      setSavingAssetId(sourceEvidenceAssetId);
      const response = await apiRequest(
        `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready`,
        "POST",
        {}
      );
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to create variant");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Image added to report as-is" });
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to add image", description: error.message, variant: "destructive" });
    },
    onSettled: () => {
      setSavingAssetId(null);
    },
  });

  const excludeMutation = useMutation({
    mutationFn: async (sourceEvidenceAssetId: number) => {
      setSavingAssetId(sourceEvidenceAssetId);
      const response = await apiRequest(
        `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready/exclude`,
        "POST",
        {},
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Image excluded from the report" });
      setExcludeCandidate(null);
      invalidate();
    },
    onError: (error: Error) => toast({ title: "Could not exclude image", description: error.message, variant: "destructive" }),
    onSettled: () => setSavingAssetId(null),
  });

  const restoreMutation = useMutation({
    mutationFn: async (sourceEvidenceAssetId: number) => {
      setSavingAssetId(sourceEvidenceAssetId);
      const response = await apiRequest(
        `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready/exclude`,
        "DELETE",
      );
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Image returned to the review queue" });
      invalidate();
    },
    onError: (error: Error) => toast({ title: "Could not restore image", description: error.message, variant: "destructive" }),
    onSettled: () => setSavingAssetId(null),
  });

  const removeMutation = useMutation({
    mutationFn: async (item: ReportReadyListItem) => {
      setSavingAssetId(item.sourceEvidenceAssetId);
      const response = await apiRequest(`/api/report-ready-images/${item.id}`, "DELETE");
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Image removed from Report Ready",
        description: "The source image is back in Retailer Ready for a new decision.",
      });
      setRemoveCandidate(null);
      invalidate();
    },
    onError: (error: Error) => toast({
      title: "Could not remove image",
      description: error.message,
      variant: "destructive",
    }),
    onSettled: () => setSavingAssetId(null),
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery<ReportReadyListResponse>({
    queryKey: ["campaign-report-ready-images", campaignId],
    queryFn: async () => {
      const response = await apiJson<ReportReadyListResponse>(
        `/api/campaigns/${campaignId}/report-ready-images?pendingLimit=${PENDING_PAGE_SIZE}`,
      );
      setExtraPendingItems([]);
      setPendingNextCursor(response.data.pending.pagination.nextCursor);
      setPendingHasMore(response.data.pending.pagination.hasMore);
      return response;
    },
    enabled: !!campaignId,
  });

  const summary = data?.data.summary;
  const preparedItems = data?.data.items ?? [];
  const excludedItems = data?.data.excluded ?? [];
  const initialPendingItems = data?.data.pending.items ?? [];
  const pendingItems = [...initialPendingItems, ...extraPendingItems];
  const totalPendingCount = summary?.pendingPreparation ?? 0;
  const remainingPendingCount = Math.max(0, totalPendingCount - pendingItems.length);
  const reviewedCount = (summary?.reportReadyCount ?? 0) + (summary?.excludedCount ?? 0);
  const totalCount = summary?.retailerReadyCount ?? 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["campaign-report-ready-images", campaignId] });
    queryClient.invalidateQueries({ queryKey: ["/api/campaigns", campaignId, "reports", "readiness"] });
  };

  const loadMorePending = async () => {
    if (!pendingNextCursor || loadingMorePending) return;

    setLoadingMorePending(true);
    try {
      const response = await apiJson<ReportReadyListResponse>(
        `/api/campaigns/${campaignId}/report-ready-images?pendingLimit=${PENDING_PAGE_SIZE}&pendingCursor=${encodeURIComponent(pendingNextCursor)}`,
      );
      setExtraPendingItems((current) => [...current, ...response.data.pending.items]);
      setPendingNextCursor(response.data.pending.pagination.nextCursor);
      setPendingHasMore(response.data.pending.pagination.hasMore);
    } finally {
      setLoadingMorePending(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          className="flex flex-row items-center justify-between space-y-0 cursor-pointer select-none"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div>
            <CardTitle>Choose Images for the Report</CardTitle>
            <p className="mt-1 text-sm font-normal text-muted-foreground">Review each approved image and choose Include, Edit, or Exclude. Included images appear in the generated report.</p>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" type="button">
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </CardHeader>
        {!isCollapsed && (
          <CardContent className="space-y-6">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading report ready summary…
              </div>
            ) : error ? (
              <div className="text-sm text-destructive">Failed to load report ready images.</div>
            ) : (
              <>
                {totalCount === 0 ? (
                  <Alert>
                    <AlertTitle>No Retailer Ready images</AlertTitle>
                    <AlertDescription>Images will appear here after they are approved as Retailer Ready.</AlertDescription>
                  </Alert>
                ) : totalPendingCount > 0 ? (
                  <Alert variant="warning">
                    <XCircle className="h-4 w-4" />
                    <AlertTitle>Review required before report generation</AlertTitle>
                    <AlertDescription>
                      {reviewedCount} of {totalCount} retailer-ready images reviewed. Decide whether to include or exclude the remaining {totalPendingCount}.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="success">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>All available images reviewed</AlertTitle>
                    <AlertDescription>
                      All {totalCount} retailer-ready images have an explicit decision. The report will use {summary?.reportReadyCount ?? 0} included image{summary?.reportReadyCount === 1 ? "" : "s"}.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <SummaryCard
                    label="Needs a Decision"
                    value={totalPendingCount}
                    hint="Choose Include, Edit, or Exclude"
                  />
                  <SummaryCard
                    label="Included in Report"
                    value={summary?.reportReadyCount ?? 0}
                    hint="Added or edited for the report"
                  />
                  <SummaryCard
                    label="Excluded"
                    value={summary?.excludedCount ?? 0}
                    hint="Reviewed and intentionally omitted"
                  />
                </div>

                <section className="space-y-3 rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">1</span>
                        <h3 className="text-base font-semibold">Images to Review</h3>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">Choose Include as shown, Edit before including, or Exclude.</p>
                    </div>
                    {totalPendingCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Showing {pendingItems.length} of {totalPendingCount}
                      </p>
                    )}
                  </div>
                  {pendingItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No images are waiting for review.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {pendingItems.map((item) => {
                          const isSavingThis = savingAssetId === item.sourceEvidenceAssetId;
                          return (
                            <ImageCard
                              key={item.sourceEvidenceAssetId}
                              imageUrl={item.secureUrl}
                              storeName={item.storeName}
                              operatorName={item.operatorName}
                              subtitle="Decision required"
                              statusLabel="Needs review"
                              actionLabel={isSavingThis ? "Including..." : "Include as shown"}
                              actionIcon={
                                isSavingThis ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                )
                              }
                              onAction={() => addAsIsMutation.mutate(item.sourceEvidenceAssetId)}
                              secondaryActionLabel="Edit before including"
                              secondaryActionIcon={<Pencil className="h-3.5 w-3.5 mr-1" />}
                              onSecondaryAction={() =>
                                setEditor({
                                  mode: "create",
                                  sourceEvidenceAssetId: item.sourceEvidenceAssetId,
                                  originalImageUrl: item.secureUrl || "",
                                  storeName: item.storeName,
                                  operatorName: item.operatorName,
                                })
                              }
                              tertiaryActionLabel="Exclude"
                              tertiaryActionIcon={<XCircle className="h-3.5 w-3.5 mr-1" />}
                              onTertiaryAction={() => setExcludeCandidate(item)}
                              isSecondaryDisabled={savingAssetId !== null}
                            />
                          );
                        })}
                      </div>
                      {pendingHasMore && (
                        <div className="flex justify-center pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={loadMorePending}
                            disabled={loadingMorePending}
                          >
                            {loadingMorePending ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Loading…
                              </>
                            ) : (
                              `Load more (${remainingPendingCount} remaining)`
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </section>

                <CollapsibleSection
                  step="2"
                  title="Included in Report"
                  count={preparedItems.length}
                  collapsed={includedCollapsed}
                  onToggle={() => setIncludedCollapsed((value) => !value)}
                >
                  {preparedItems.length === 0 ? <p className="text-sm text-muted-foreground">No images have been included yet.</p> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {preparedItems.map((item) => {
                        const isSavingThis = savingAssetId === item.sourceEvidenceAssetId;
                        return (
                          <ImageCard key={item.id} imageUrl={item.secureUrl} storeName={item.storeName} operatorName={item.operatorName}
                            cropRecipe={item.cropRecipe}
                            subtitle={`Report Ready ${format(new Date(item.updatedAt), "MMM d, yyyy")}`} statusLabel="Report Ready"
                            actionLabel="Edit report image" actionIcon={<Pencil className="h-3.5 w-3.5 mr-1" />}
                            onAction={() => setEditor({ mode: "edit", reportReadyImageId: item.id, sourceEvidenceAssetId: item.sourceEvidenceAssetId,
                              originalImageUrl: item.sourceSecureUrl || item.secureUrl || "", storeName: item.storeName, operatorName: item.operatorName })}
                            tertiaryActionLabel={isSavingThis ? "Removing..." : "Remove from report"}
                            tertiaryActionIcon={isSavingThis
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                            onTertiaryAction={() => setRemoveCandidate(item)}
                            isSecondaryDisabled={savingAssetId !== null} />
                        );
                      })}
                    </div>
                  )}
                </CollapsibleSection>

                <CollapsibleSection title="Excluded from report" count={excludedItems.length} collapsed={excludedCollapsed}
                  onToggle={() => setExcludedCollapsed((value) => !value)}>
                  {excludedItems.length === 0 ? <p className="text-sm text-muted-foreground">No images excluded.</p> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {excludedItems.map((item) => (
                        <ImageCard key={item.exclusionId} imageUrl={item.secureUrl} storeName={item.storeName} operatorName={item.operatorName}
                          subtitle={`Excluded ${format(new Date(item.excludedAt), "MMM d, yyyy")}`} statusLabel="Excluded"
                          actionLabel={savingAssetId === item.sourceEvidenceAssetId ? "Restoring..." : "Return to review"}
                          actionIcon={savingAssetId === item.sourceEvidenceAssetId ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                          onAction={() => restoreMutation.mutate(item.sourceEvidenceAssetId)} isSecondaryDisabled={savingAssetId !== null} />
                      ))}
                    </div>
                  )}
                </CollapsibleSection>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {editor && (
        <ReportReadyEditorModal
          open={!!editor}
          onOpenChange={(open) => !open && setEditor(null)}
          mode={editor.mode}
          sourceEvidenceAssetId={editor.sourceEvidenceAssetId}
          reportReadyImageId={editor.mode === "edit" ? editor.reportReadyImageId : undefined}
          originalImageUrl={editor.originalImageUrl}
          storeName={editor.storeName}
          operatorName={editor.operatorName}
          onSaved={invalidate}
        />
      )}

      <AlertDialog open={!!excludeCandidate} onOpenChange={(open) => !open && setExcludeCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exclude this image from the report?</AlertDialogTitle>
            <AlertDialogDescription>
              This records an explicit decision to omit the image. The original evidence remains unchanged, and you can return it to the review queue later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep reviewing</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => excludeCandidate && excludeMutation.mutate(excludeCandidate.sourceEvidenceAssetId)}>
              Exclude from report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeCandidate} onOpenChange={(open) => !open && setRemoveCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this image from Report Ready?</AlertDialogTitle>
            <AlertDialogDescription>
              The prepared version will be removed from future reports, and the original image will return to Retailer Ready for a new Add, Edit, or Exclude decision. Reports already generated will not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep in report</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeCandidate && removeMutation.mutate(removeCandidate)}
            >
              {removeMutation.isPending ? "Removing..." : "Remove from report"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SummaryCard({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

function ImageCard({
  imageUrl,
  storeName,
  operatorName,
  subtitle,
  actionLabel,
  actionIcon,
  onAction,
  secondaryActionLabel,
  secondaryActionIcon,
  onSecondaryAction,
  isSecondaryDisabled,
  tertiaryActionLabel,
  tertiaryActionIcon,
  onTertiaryAction,
  statusLabel,
  cropRecipe,
}: {
  imageUrl: string | null;
  storeName: string;
  operatorName: string;
  subtitle: string;
  actionLabel: string;
  actionIcon: React.ReactNode;
  onAction: () => void;
  secondaryActionLabel?: string;
  secondaryActionIcon?: React.ReactNode;
  onSecondaryAction?: () => void;
  isSecondaryDisabled?: boolean;
  tertiaryActionLabel?: string;
  tertiaryActionIcon?: React.ReactNode;
  onTertiaryAction?: () => void;
  statusLabel?: string;
  cropRecipe?: {
    crop?: {
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
  } | null;
}) {
  return (
    <div className="bg-card border rounded-lg overflow-hidden">
      <div className={`relative w-full overflow-hidden bg-muted ${statusLabel === "Report Ready" ? "aspect-[4/7]" : "aspect-square"}`}>
        {statusLabel && <Badge className="absolute left-2 top-2 z-10" variant={statusLabel === "Report Ready" ? "default" : statusLabel === "Excluded" ? "secondary" : "outline"}>{statusLabel}</Badge>}
        {imageUrl ? (
          cropRecipe?.crop ? (
            <div
              className="absolute"
              style={{
                width: `${100 / cropRecipe.crop.width}%`,
                height: `${100 / cropRecipe.crop.height}%`,
                left: `${-(cropRecipe.crop.x / cropRecipe.crop.width) * 100}%`,
                top: `${-(cropRecipe.crop.y / cropRecipe.crop.height) * 100}%`,
              }}
            >
              <img src={imageUrl} alt={storeName} className="w-full h-full object-cover" />
            </div>
          ) : (
            <img src={imageUrl} alt={storeName} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
            No preview
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="text-sm font-semibold line-clamp-1">{storeName}</div>
        <div className="text-xs text-muted-foreground">{operatorName}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
        <div className="space-y-1.5 pt-1">
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={onSecondaryAction}
              disabled={isSecondaryDisabled}
            >
              {secondaryActionIcon}
              {secondaryActionLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onAction}
            disabled={isSecondaryDisabled}
          >
            {actionIcon}
            {actionLabel}
          </Button>
          {tertiaryActionLabel && onTertiaryAction && (
            <Button type="button" variant="ghost" size="sm" className="w-full text-destructive hover:text-destructive"
              onClick={onTertiaryAction} disabled={isSecondaryDisabled}>
              {tertiaryActionIcon}{tertiaryActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, count, collapsed, onToggle, children, step }: {
  title: string; count: number; collapsed: boolean; onToggle: () => void; children: React.ReactNode; step?: string;
}) {
  return (
    <section className="space-y-3 border-t pt-5">
      <button type="button" className="flex w-full items-center justify-between text-left" onClick={onToggle}>
        <span className="flex items-center gap-2 font-semibold">
          {step && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{step}</span>}
          {title}<Badge variant="secondary">{count}</Badge>
        </span>
        {collapsed ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
      </button>
      {!collapsed && children}
    </section>
  );
}
